/**
 * SplashScreen.js - A terminal-like loading splash screen
 */
import './SplashScreen.css';

class SplashScreen {
  constructor(container) {
    this.container = container;
    this.typeSpeed = 60; // milliseconds per character
    this.isVisible = true;
    
    // Bind methods
    this.hide = this.hide.bind(this);
  }
  
  /**
   * Initialize the splash screen
   */
  init() {
    // Create splash screen elements with prompt, input, and cursor in a single span for perfect wrapping and alignment
    this.container.innerHTML = `
      <div class="splash-screen">
        <div class="splash-terminal">
          <div class="splash-terminal-line"><span class="splash-prompt-input"><span class="splash-prompt">the-0-to-1-machine@Mac ~ %</span> <span class="splash-input"></span><span class="splash-cursor">_</span></span></div>
          <div class="splash-output"></div>
        </div>
      </div>
    `;
    
    this.terminalInput = this.container.querySelector('.splash-input');
    this.terminalOutput = this.container.querySelector('.splash-output');
    this.splashScreen = this.container.querySelector('.splash-screen');
    this.cursor = this.container.querySelector('.splash-cursor');
    
    // Start the animation sequence
    setTimeout(() => this.startAnimation(), 1000);
    
    return this;
  }
  
  /**
   * Start the animation sequence
   */
  startAnimation() {
    // Type the command
    this.typeText('npm install @our-appologies/studio --global', this.terminalInput)
      .then(() => this.wait(500))
      .then(() => {
        // Simulate package installation directly without showing the command again
        return this.simulatePackageInstallation();
      })
      .then(() => this.wait(800))
      .then(() => {
        // Add our-appologies init command line
        this.addOutputLine('<span class="splash-prompt">the-0-to-1-machine@Mac ~ %</span><span class="splash-command">our-appologies init</span>');
        return this.wait(500);
      })
      .then(() => {
        // Simulate initialization
        return this.simulateInitialization();
      })
      .then(() => this.wait(500))
      .then(() => {
        // Add final boot message
        this.addOutputLine('<span class="splash-boot-message">Booting up \'our-[app]ologies\' -- please standby...</span>');
        
        // Hide splash screen after a delay
        return this.wait(1500);
      })
      .then(() => {
        this.hide();
      });
  }
  
  /**
   * Type text with a typewriter effect
   * @param {string} text - Text to type
   * @param {HTMLElement} element - Element to type into
   * @returns {Promise} - Resolves when typing is complete
   */
  typeText(text, element) {
    return new Promise(resolve => {
      let i = 0;
      const interval = setInterval(() => {
        element.textContent += text.charAt(i);
        i++;
        
        if (i >= text.length) {
          clearInterval(interval);
          resolve();
        }
      }, this.typeSpeed);
    });
  }
  
  /**
   * Wait for a specified time
   * @param {number} ms - Milliseconds to wait
   * @returns {Promise} - Resolves after the wait time
   */
  wait(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
  
  /**
   * Add a line to the terminal output
   * @param {string} html - HTML content of the line
   */
  addOutputLine(html) {
    const line = document.createElement('div');
    line.className = 'splash-terminal-line';
    line.innerHTML = html;
    this.terminalOutput.appendChild(line);
    
    // Auto-scroll to keep the latest content in view
    this.splashScreen.scrollTo({
      top: this.splashScreen.scrollHeight,
      behavior: 'smooth'
    });
    
    return line;
  }
  
  /**
   * Simulate package installation with realistic npm output
   * @returns {Promise} - Resolves when animation is complete
   */
  simulatePackageInstallation() {
    const steps = [
      '<span class="splash-npm-warn">npm WARN deprecated legacy-peer-deps@1.0.0: We\'re not sorry about this one</span>',
      '<span class="splash-npm-warn">npm WARN deprecated apology-blocker@2.1.1: Unapologetically awesome since 2024</span>',
      '',
      '<span class="splash-npm-text">> @our-appologies/studio@3.1.4 preinstall</span>',
      '<span class="splash-npm-text">> node scripts/pre-flight-check.js</span>',
      '',
      '<span class="splash-check">✓ Checking creative dependencies...</span>',
      '<span class="splash-check">✓ Validating design principles...</span>',
      '<span class="splash-check">✓ Scanning for remorse... None found (as expected)</span>',
      '<span class="splash-check">✓ Loading unapologetic frameworks...</span>',
      '',
      '<span class="splash-npm-info">added 2,048 packages from 512 contributors and audited 4,096 packages in 3.7s</span>',
      '',
      '<span class="splash-npm-text">147 packages are looking for funding</span>',
      '<span class="splash-npm-text">  run `<span class="splash-highlight">npm fund</span>` for details (we\'re not sorry about capitalism)</span>',
      '',
      '<span class="splash-npm-success">found 0 vulnerabilities (because we don\'t apologize for our code)</span>'
    ];
    
    return steps.reduce((promise, step, index) => {
      return promise
        .then(() => this.wait(step === '' ? 150 : 250)) // Wait between steps
        .then(() => step === '' ? this.addOutputLine('&nbsp;') : this.addOutputLine(step));
    }, Promise.resolve());
  }
  
  /**
   * Simulate initialization with progress bar
   * @returns {Promise} - Resolves when animation is complete
   */
  simulateInitialization() {
    return new Promise(resolve => {
      // Initial message
      this.addOutputLine('<span class="splash-npm-success">🚀 Initializing our-[app]ologies studio environment...</span>');
      
      // Create the progress bar with brackets
      const progressWrapper = document.createElement('div');
      progressWrapper.className = 'splash-progress-wrapper';
      
      // Add opening bracket
      const openBracket = document.createElement('span');
      openBracket.className = 'splash-progress-bracket';
      openBracket.textContent = '[';
      progressWrapper.appendChild(openBracket);
      
      // Create progress container
      const progressContainer = document.createElement('div');
      progressContainer.className = 'splash-progress-container';
      
      const progressBar = document.createElement('div');
      progressBar.className = 'splash-progress-bar';
      
      progressContainer.appendChild(progressBar);
      progressWrapper.appendChild(progressContainer);
      
      // Add closing bracket
      const closeBracket = document.createElement('span');
      closeBracket.className = 'splash-progress-bracket';
      closeBracket.textContent = ']';
      progressWrapper.appendChild(closeBracket);
      
      // Add percentage text (initially empty)
      const percentText = document.createElement('span');
      percentText.className = 'splash-progress-percentage';
      percentText.textContent = '';
      progressWrapper.appendChild(percentText);
      
      this.terminalOutput.appendChild(progressWrapper);
      
      // Auto-scroll after adding the progress bar
      this.splashScreen.scrollTo({
        top: this.splashScreen.scrollHeight,
        behavior: 'smooth'
      });
      
      // Animate progress bar
      setTimeout(() => {
        progressBar.style.width = '100%';
      }, 100);
      
      // Show "100%" after progress bar completes
      setTimeout(() => {
        percentText.textContent = ' 100%';
        this.addOutputLine('&nbsp;');
        this.addOutputLine('<span class="splash-npm-success">Ready to build something amazing?</span>');
        resolve();
      }, 960);
    });
  }
  
  /**
   * Hide the splash screen with a fade out effect
   */
  hide() {
    this.splashScreen.classList.add('fade-out');
    
    // Remove from DOM after animation
    setTimeout(() => {
      this.isVisible = false;
      this.container.innerHTML = '';
      
      // Dispatch event that splash screen is complete
      const event = new CustomEvent('splashscreen:complete', {
        bubbles: true
      });
      this.container.dispatchEvent(event);
    }, 1000); // Match this with the CSS transition duration
  }
}

export default SplashScreen; 